import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField, RecordFieldProvenance } from "@/components/record/fields";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ApiError, apiFetch } from "@/lib/api";
import type { ActivityResource, Page, RenewalResource } from "@/lib/api-types";
import { useIsOwner, useIsReadOnly } from "@/lib/current-actor";
import { RENEWAL_NEXT_EVENT, RENEWAL_OWNER_INTENT_LABELS, RENEWAL_STATUS_LABELS } from "@/lib/enum-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

/**
 * `/renewals/:id` (Story 11.1, spec judgement calls 4/5). Built on
 * `RecordPage`/`FieldRow`, mirroring `MunicipalityRecord.tsx`'s own shell
 * usage — never a parallel page implementation. **Deliberately the "plain
 * record-page shell... no custom stage-pipeline widget yet" the spec asks
 * for**: almost every field renders read-only (Story 11.3's own board is the
 * real editing/moving surface for the pipeline itself; this page exists so
 * the record is reachable and its current pipeline position is honestly
 * visible, nothing more). The one real Owner action this page offers beyond
 * field edits is advancing the stage machine one event at a time via
 * `RENEWAL_NEXT_EVENT` (plain, ordinary `Negotiating` branches to a
 * two-button choice instead) and, from `Agreed - renewing`/`Agreed -
 * vacating`, signing — both call the real API routes `app/api/renewals.py`
 * builds, never a bare status write.
 *
 * No "Renewals" list screen exists yet (spec judgement call 5) — the only
 * way here is a direct URL, a link from the source Lease's own record page
 * (`LeaseRecord.tsx`), or a card on the renewal board (Story 11.3).
 *
 * Story 11.4 adds the conversation log below the transition buttons —
 * `Activity`/`entity_type="renewal"`, reused wholesale from
 * `TenantRecord.tsx`'s own communication-log section (Story 6.2's own
 * explicit "built once, reused... without modification" design). Same
 * query shape, same append-only form, same `!isReadOnly` (not `isOwner`)
 * gate: `POST /activities` is `role=Role.MEMBER`, broader than every other
 * write on this page, which stays Owner-only per Story 11.1.
 *
 * **Story 11.5 (R-N10, R-N11)** is this page's first real departure from
 * "every field renders read-only": `notice_served_on`/`notice_served_method`
 * become genuinely inline-editable (Owner-only), reusing Story 2.3/2.5's
 * exact `FieldRow`/`InlineEditCell` mechanism `MunicipalityRecord.tsx`
 * already proves — recording `notice_served_on` is a real Owner action, the
 * `renewal-notice-serve-by` alert's own terminal condition, so it needs a
 * real write path, not just visibility. `serve_by` (computed, never a
 * stored column) renders next to `notice_period_days`, reusing `FieldRow`'s
 * existing UX-DR8 provenance marker wholesale — mirrors `LeaseRecord.tsx`'s
 * own identical `notify_by` treatment. Two small, plain buttons below the
 * notice-period field ("I've confirmed this" / "Counsel has confirmed
 * this", Owner-only) are R-N11's own real, explicit confirmation action —
 * mirrors this page's own existing plain-button style for the
 * stage-transition buttons (spec judgement call 2), not a new dialog.
 */

const ORDERED_FIELD_KEYS: (keyof RenewalResource & string)[] = [
  "status",
  "owner_intent",
  "term",
  "current_rent_cents",
  "proposed_rent_cents",
  "non_renewal_cause",
  "notice_type",
  "notice_period_days",
  "serve_by",
  "intended_termination_date",
  "notice_served_on",
  "notice_served_method",
  "market_note",
  "date_owner_notified_tenant",
  "tenant_response",
  "tenant_response_date",
  "next_follow_up_date",
  "new_lease_id",
];

//: Story 11.5's own AC, verbatim — the record page's honest rendering when
//: `serve_by.date` is `null` (either `intended_termination_date` or `notice_
//: period_days` is unset). Never a crash, never a nonsense date.
const NO_SERVE_BY_MESSAGE =
  "No serve-by date yet — needs an intended termination date and a notice period";

function formatServeBy(serveBy: RenewalResource["serve_by"]): string {
  return serveBy.date ? formatDateCell(serveBy.date) : NO_SERVE_BY_MESSAGE;
}

/** `app.domain.renewals.serve_by.ServeByTier`'s two real confirmation
 * values (`owner_confirmed`/`counsel_confirmed`) map onto `RecordField
 * Provenance`'s own existing `"confirmed"` tier — mirrors `LeaseRecord.tsx`'s
 * own identical `notify_by.tier === "lease_explicit" ? "confirmed" : ...`
 * mapping for the identical "a backend tier this shared frontend type does
 * not itself need to distinguish" reason (`app/domain/renewals/serve_by.py`'s
 * own module docstring has the full reasoning). `app_default` passes
 * through unchanged — it is already one of `RecordFieldProvenance`'s own
 * three legal tier values.
 *
 * A `null` `serve_by.date` renders no provenance at all — mirrors `Lease
 * Record.tsx`'s own `notifyByProvenance` precedent exactly: there is no date
 * on screen for the marker to be attached to, so there is nothing to warn
 * about (the AC's own honest-sentence rendering, `formatServeBy` above,
 * already covers that state on its own). */
function serveByProvenance(serveBy: RenewalResource["serve_by"]): RecordFieldProvenance | undefined {
  if (serveBy.date === null) {
    return undefined;
  }
  return {
    tier: serveBy.tier === "app_default" ? "app_default" : "confirmed",
    confirmedOn: serveBy.confirmed_on,
  };
}

export function RenewalRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isOwner = useIsOwner();
  const isReadOnly = useIsReadOnly();
  const [transitioning, setTransitioning] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | undefined>(undefined);
  //: Story 11.5's own real, explicit R-N11 confirmation action — mirrors
  //: `transitioning`'s identical "disable the button while the request is
  //: in flight" shape.
  const [confirmingNoticePeriod, setConfirmingNoticePeriod] = useState(false);

  const queryKey = useMemo(() => ["renewals", "record", id] as const, [id]);
  const activitiesQueryKey = useMemo(() => ["renewals", "record", id, "activities"] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<RenewalResource>(`/renewals/${id}`),
    enabled: id !== undefined,
  });

  //: Story 11.4, mirrors `TenantRecord.tsx`'s identical VC-6.2-11 shape: a
  //: real, always-issued `GET /activities?entity_type=renewal&entity_id=...`
  //: request, scoped to this renewal and only this renewal.
  const activitiesQuery = useQuery({
    queryKey: activitiesQueryKey,
    queryFn: () =>
      apiFetch<Page<ActivityResource>>(
        `/activities?entity_type=renewal&entity_id=${encodeURIComponent(id ?? "")}`,
      ),
    enabled: id !== undefined,
  });

  useEffect(() => {
    document.title = recordQuery.data
      ? `Renewal — ${recordQuery.data.term} — PropDesk`
      : "Renewal — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: RenewalResource) {
    queryClient.setQueryData<RenewalResource>(queryKey, updated);
  }

  //: Story 11.4, mirrors `TenantRecord.tsx::handleAddNote` exactly: always a
  //: new `POST /activities` row (no update path exists), refetches the log
  //: afterward rather than a hand-rolled cache splice so the newest-first
  //: order `app/repo/activities.py` itself owns is always what renders.
  async function handleAddNote() {
    if (id === undefined) {
      return;
    }
    const trimmed = noteBody.trim();
    if (trimmed === "") {
      return;
    }
    setIsSubmittingNote(true);
    setNoteError(undefined);
    try {
      await apiFetch<ActivityResource>("/activities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entity_type: "renewal", entity_id: id, body: trimmed }),
      });
      setNoteBody("");
      await queryClient.invalidateQueries({ queryKey: activitiesQueryKey });
    } catch (error) {
      setNoteError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmittingNote(false);
    }
  }

  //: Story 11.5, spec judgement calls 1-2: two small, plain buttons near the
  //: notice-period field, Owner-only. Calls `POST /renewals/{id}/confirm-
  //: notice-period` — the one real write path for R-N11's own confirmation
  //: tier (never inferred from typing a value into `notice_period_days`).
  async function confirmNoticePeriod(confirmedBy: "owner" | "counsel") {
    if (id === undefined) {
      return;
    }
    setConfirmingNoticePeriod(true);
    try {
      const updated = await apiFetch<RenewalResource>(`/renewals/${id}/confirm-notice-period`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmed_by: confirmedBy }),
      });
      writeRecordToCache(updated);
      showToast(
        confirmedBy === "owner" ? "Notice period confirmed." : "Counsel's confirmation recorded.",
      );
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message);
        return;
      }
      throw error;
    } finally {
      setConfirmingNoticePeriod(false);
    }
  }

  //: Story 11.5: the first fields on this page to become genuinely
  //: inline-editable (`notice_served_on`/`notice_served_method`) — mirrors
  //: `MunicipalityRecord.tsx`'s own `sendPatch`/`commitEdit` shape exactly,
  //: this page's own first real use of `RecordPage`'s `onCommitEdit` prop.
  async function sendPatch(
    row: RenewalResource,
    field: DataTableColumn<RenewalResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    if (payload.kind !== "value") {
      throw new Error(`unexpected transition-kind payload for field "${field.key}"`);
    }
    try {
      const updated = await apiFetch<RenewalResource>(`/renewals/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field.key]: payload.value === "" ? null : payload.value }),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<RenewalResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  async function fireEvent(path: string, event?: string) {
    setTransitioning(true);
    try {
      const updated = await apiFetch<RenewalResource>(path, {
        method: event === undefined ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        ...(event === undefined ? {} : { body: JSON.stringify({ event }) }),
      });
      writeRecordToCache(updated);
      showToast(`Renewal moved to ${RENEWAL_STATUS_LABELS[updated.status] ?? updated.status}.`);
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message);
        return;
      }
      throw error;
    } finally {
      setTransitioning(false);
    }
  }

  const fields = useMemo<RecordField<RenewalResource>[]>(() => {
    return ORDERED_FIELD_KEYS.map((key) => {
      if (key === "status") {
        return { key, render: (r: RenewalResource) => RENEWAL_STATUS_LABELS[r.status] ?? r.status };
      }
      if (key === "owner_intent") {
        return {
          key,
          render: (r: RenewalResource) => RENEWAL_OWNER_INTENT_LABELS[r.owner_intent] ?? r.owner_intent,
        };
      }
      if (key === "current_rent_cents" || key === "proposed_rent_cents") {
        return { key, render: (r: RenewalResource) => formatMoneyCell(r[key]) };
      }
      if (key === "serve_by") {
        return {
          key,
          render: (r: RenewalResource) => formatServeBy(r.serve_by),
          provenance: (r: RenewalResource) => serveByProvenance(r.serve_by),
        };
      }
      // Story 11.5: the only two genuinely inline-editable fields on this
      // page (top-of-file doc comment) — Owner-only, mirrors `Municipality
      // Record.tsx`'s own identical `isOwner ? { edit: ... } : {}` gate.
      if (key === "notice_served_on" || key === "notice_served_method") {
        return { key, ...(isOwner ? { edit: { kind: "text" as const } } : {}) };
      }
      return { key };
    });
  }, [isOwner]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;
  const row = recordQuery.data;
  const nextEvent = row ? RENEWAL_NEXT_EVENT[row.status] : undefined;
  const isNegotiating = row?.status === "negotiating";
  const isAgreed = row?.status === "agreed_renewing" || row?.status === "agreed_vacating";

  return (
    <div className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />

      <RecordPage<RenewalResource>
        fields={fields}
        row={row}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(r) => `Renewal — ${r.term}`}
        backLink={{ label: "Renewal pipeline", href: "/tenants-leases/renewals" }}
        tint="amber"
        onCommitEdit={commitEdit}
      />

      {/*
        Story 11.5, spec judgement calls 1-2: R-N11's own real, explicit
        confirmation action — two small, plain buttons near the
        notice-period field, Owner-only, mirrors the stage-transition
        buttons' own plain style immediately below rather than a new
        dialog. Hidden once `serve_by.tier` is no longer `app_default` —
        there is nothing further to confirm once a real confirmation is
        already on file (re-confirming would just be noise), and its own
        disappearance is the visible proof the action actually landed
        (VC-11.5-14).
      */}
      {isOwner && row && row.serve_by.tier === "app_default" ? (
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={confirmingNoticePeriod}
            onClick={() => void confirmNoticePeriod("owner")}
          >
            I&rsquo;ve confirmed this
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={confirmingNoticePeriod}
            onClick={() => void confirmNoticePeriod("counsel")}
          >
            Counsel has confirmed this
          </Button>
        </div>
      ) : null}

      {isOwner && row ? (
        <div className="mt-6 flex flex-wrap gap-3">
          {nextEvent ? (
            <Button
              type="button"
              disabled={transitioning}
              onClick={() => void fireEvent(`/renewals/${row.id}/status`, nextEvent.event)}
            >
              {nextEvent.label}
            </Button>
          ) : null}
          {isNegotiating ? (
            <>
              <Button
                type="button"
                disabled={transitioning}
                onClick={() => void fireEvent(`/renewals/${row.id}/status`, "agree_renewing")}
              >
                Agree — renewing
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={transitioning}
                onClick={() => void fireEvent(`/renewals/${row.id}/status`, "agree_vacating")}
              >
                Agree — vacating
              </Button>
            </>
          ) : null}
          {isAgreed ? (
            <Button type="button" disabled={transitioning} onClick={() => void fireEvent(`/renewals/${row.id}/sign`)}>
              Sign
            </Button>
          ) : null}
        </div>
      ) : null}

      {/*
        Story 11.4's conversation log — reused wholesale from
        `TenantRecord.tsx`'s own communication-log section (Story 6.2's own
        "built once, reused... without modification" design): free text,
        stamped with date and author, appended rather than replacing, no
        edit affordance anywhere (there is no `PATCH /activities/{id}`
        route). `!isReadOnly` — not `isOwner` — mirrors `POST /activities`'s
        own `role=Role.MEMBER`: a Member sees the form, only Read-only does
        not, even though every other write on this page is Owner-only.
      */}
      <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
        <h2 className="text-heading font-semibold text-ink">Conversation log</h2>
        {!isReadOnly ? (
          <form
            className="mt-4 flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void handleAddNote();
            }}
          >
            <label className="flex flex-col gap-1">
              <span className="sr-only">Add a note</span>
              <textarea
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
                placeholder="What happened? For example: Called about the renewal — she wants to stay."
                rows={3}
                className="w-full rounded-md border border-border-input bg-surface px-3 py-2 text-body text-ink placeholder-ink-tertiary focus:border-border-focus"
              />
            </label>
            {noteError ? (
              <p role="alert" className="text-meta text-danger">
                {noteError}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button type="submit" disabled={noteBody.trim() === "" || isSubmittingNote}>
                {isSubmittingNote ? "Saving…" : "Add note"}
              </Button>
            </div>
          </form>
        ) : null}
        <div className="mt-4">
          {(activitiesQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-body text-ink-secondary">No notes yet.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {(activitiesQuery.data?.data ?? []).map((note) => (
                <li key={note.id} className="rounded-lg border border-border-hairline bg-surface p-4">
                  <p className="whitespace-pre-wrap text-body text-ink">{note.body}</p>
                  <p className="mt-2 text-meta text-ink-secondary">
                    {formatDateCell(note.created_at)}
                    {note.author_display_name ? ` — ${note.author_display_name}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
